import { useEffect, useState } from "react";
import { CloudOff, RefreshCw, Loader2 } from "lucide-react";
import { subscribe, flush } from "../../pwa/podOfflineQueue";

/**
 * Compact pending-uploads pill shown in the driver header. Hidden when the
 * queue is empty; otherwise displays the pending count with a manual retry
 * button. Auto-flushes on `online` events (see podOfflineQueue.js) so this
 * is rarely needed, but ops + drivers find it reassuring.
 */
export default function PendingUploadsBadge() {
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => subscribe(setCount), []);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  if (count === 0) return null;

  const retry = async () => {
    setBusy(true);
    try { await flush(); } finally { setBusy(false); }
  };

  return (
    <button
      onClick={retry}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
        online
          ? "bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200"
          : "bg-slate-100 text-slate-600 border border-slate-200"
      }`}
      title={online ? "Retry pending POD uploads" : "Offline — queued POD uploads will retry once you reconnect"}
      data-testid="pod-pending-badge"
    >
      {busy ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : online ? (
        <RefreshCw className="w-3 h-3" />
      ) : (
        <CloudOff className="w-3 h-3" />
      )}
      <span data-testid="pod-pending-count">{count}</span>
      <span className="hidden sm:inline">pending</span>
    </button>
  );
}
